const STORAGE_PREFIX = "reimagine_report_viewed";

export const REPORT_READ_STATE_CHANGED = "report-read-state-changed";

function getStorageKey(
  userId: number | string,
  projectId: number | string
): string {
  return `${STORAGE_PREFIX}:${userId}:${projectId}`;
}

function notifyReadStateChanged(detail: {
  userId: number | string;
  projectId: number | string;
  latestReportDate: string;
}): void {
  try {
    window.dispatchEvent(new CustomEvent(REPORT_READ_STATE_CHANGED, { detail }));
  } catch {
    // Ignore environments without window/CustomEvent
  }
}

export function normalizeClientReportStatus(value: unknown): string {
  return String(value || "submitted").trim().toLowerCase();
}

/** Only submitted reports are visible to clients and should drive sidebar "new report" badges. */
export function isClientVisibleReport(
  report: { status?: string | null } | null | undefined
): boolean {
  return normalizeClientReportStatus(report?.status) === "submitted";
}

export function getClientVisibleReports<T extends { status?: string | null }>(
  reports: T[] | null | undefined
): T[] {
  if (!Array.isArray(reports)) {
    return [];
  }
  return reports.filter(isClientVisibleReport);
}

/**
 * Latest report date across a report list (ISO string), or null when none.
 */
export function getLatestReportDate(
  reports: Array<{
    reportDate?: string | null;
    submittedAt?: string | null;
  }> | null | undefined
): string | null {
  if (!Array.isArray(reports) || reports.length === 0) {
    return null;
  }

  let latestMs = 0;
  for (const report of reports) {
    const raw = report?.submittedAt || report?.reportDate;
    if (!raw) continue;
    const ms = new Date(raw).getTime();
    if (!Number.isNaN(ms) && ms > latestMs) {
      latestMs = ms;
    }
  }

  return latestMs > 0 ? new Date(latestMs).toISOString() : null;
}

/** Latest submitted report timestamp — used for client unread indicators (ignores saved drafts). */
export function getLatestClientReportDate(
  reports: Array<{
    reportDate?: string | null;
    submittedAt?: string | null;
    status?: string | null;
  }> | null | undefined
): string | null {
  return getLatestReportDate(getClientVisibleReports(reports));
}

/**
 * Records that the client has viewed the reports for this project.
 * Stores the latest report date (when known) so a newer report can still surface later.
 */
export function markReportsViewed(
  userId: number | string | null | undefined,
  projectId: number | string | null | undefined,
  latestReportDate?: string | null
): void {
  if (userId == null || projectId == null) {
    return;
  }

  const storedDate = latestReportDate || new Date().toISOString();

  try {
    localStorage.setItem(getStorageKey(userId, projectId), storedDate);
    notifyReadStateChanged({
      userId,
      projectId,
      latestReportDate: storedDate,
    });
  } catch {
    // Ignore storage errors (private mode, quota, etc.)
  }
}

/**
 * Returns true when there is a report the client has not seen yet.
 * Requires latestReportDate — without a report there is nothing to notify about.
 */
export function areReportsUnread(
  userId: number | string | null | undefined,
  projectId: number | string | null | undefined,
  latestReportDate?: string | null
): boolean {
  if (userId == null || projectId == null || !latestReportDate) {
    return false;
  }

  try {
    const seen = localStorage.getItem(getStorageKey(userId, projectId));
    if (!seen) {
      return true;
    }
    return new Date(latestReportDate) > new Date(seen);
  } catch {
    return false;
  }
}
